import { DependencyList, useEffect, useState } from 'react';

export type ScrollFadeEdges = {
  showTopFade: boolean;
  showBottomFade: boolean;
};

const SCROLL_EPSILON = 2;

/** Fade visibility via scroll sentinels (works with Perfect Scrollbar). */
export function useIntersectionScrollFadeEdges(
  root: HTMLElement | null,
  topSentinel: HTMLElement | null,
  bottomSentinel: HTMLElement | null,
  deps: DependencyList = [],
): ScrollFadeEdges {
  const [topVisible, setTopVisible] = useState(true);
  const [bottomVisible, setBottomVisible] = useState(true);
  const [canScroll, setCanScroll] = useState(false);

  useEffect(() => {
    if (!root) {
      setCanScroll(false);
      return;
    }

    const updateCanScroll = () => {
      setCanScroll(root.scrollHeight - root.clientHeight > SCROLL_EPSILON);
    };

    updateCanScroll();
    const resizeObserver = new ResizeObserver(updateCanScroll);
    resizeObserver.observe(root);

    return () => resizeObserver.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [root, ...deps]);

  useEffect(() => {
    if (!root || !topSentinel) {
      setTopVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setTopVisible(entry.isIntersecting),
      { root, threshold: 0 },
    );
    observer.observe(topSentinel);

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [root, topSentinel, ...deps]);

  useEffect(() => {
    if (!root || !bottomSentinel) {
      setBottomVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setBottomVisible(entry.isIntersecting),
      { root, threshold: 0 },
    );
    observer.observe(bottomSentinel);

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [root, bottomSentinel, ...deps]);

  return {
    showTopFade: canScroll && !topVisible,
    showBottomFade: canScroll && !bottomVisible,
  };
}
